import { useEffect, useState, type CSSProperties, type ReactEventHandler } from "react";
import { createSignedStorageThumbnailUrl } from "@/lib/storageImageUrls";

export function StorageThumbnailImage({
  src,
  bucket,
  alt,
  className,
  onError,
  style,
  width = 320,
  height = 180,
  quality = 55,
  resize = "cover",
}: {
  src: string;
  bucket?: string;
  alt: string;
  className?: string;
  onError?: ReactEventHandler<HTMLImageElement>;
  style?: CSSProperties;
  width?: number;
  height?: number;
  quality?: number;
  resize?: "cover" | "contain" | "fill";
}) {
  const [thumbnailUrl, setThumbnailUrl] = useState(src);
  const [hasThumbnailError, setHasThumbnailError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setHasThumbnailError(false);
    setThumbnailUrl(src);
    void createSignedStorageThumbnailUrl(src, {
      bucket,
      width,
      height,
      quality,
      resize,
    })
      .then((url) => {
        if (!cancelled) {
          setThumbnailUrl(url);
          setHasThumbnailError(false);
        }
      })
      .catch(() => {
        if (!cancelled) setThumbnailUrl(src);
      });

    return () => {
      cancelled = true;
    };
  }, [bucket, height, quality, resize, src, width]);

  const handleImageError: ReactEventHandler<HTMLImageElement> = (event) => {
    if (!hasThumbnailError && thumbnailUrl !== src) {
      setHasThumbnailError(true);
      setThumbnailUrl(src);
    }
    onError?.(event);
  };

  return (
    <img
      src={thumbnailUrl}
      alt={alt}
      className={className}
      style={style}
      loading="lazy"
      decoding="async"
      onError={handleImageError}
    />
  );
}
